import { Component } from '@theme/component';

/**
 * @typedef {Object} ProductTabsRefs
 * @property {HTMLElement[]} [tabs] - The tab buttons.
 * @property {HTMLElement[]} [panels] - The product grids, one per tab.
 */

/**
 * A row of collection tabs that swaps the product grid below it.
 *
 * All panels are rendered server-side; only visibility is toggled, so the first
 * panel is on screen before this module executes and with JS disabled.
 *
 * @extends {Component<ProductTabsRefs>}
 */
export class ProductTabsComponent extends Component {
  #active = 0;

  connectedCallback() {
    super.connectedCallback();

    const { tabs } = this.refs;
    if (!tabs?.length) return;

    // Seed from the DOM: the theme editor replaces the whole section on every
    // setting change, so private-field state does not survive. Liquid is the
    // source of truth for the initial index.
    this.#active = Math.max(
      0,
      tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true')
    );

    this.#render();
  }

  updatedCallback() {
    super.updatedCallback();

    const { tabs } = this.refs;
    if (!tabs?.length) return;

    // A morph restores the server's aria-selected. Re-assert the live index.
    this.#active = Math.min(this.#active, tabs.length - 1);

    this.#render();
  }

  /**
   * Reveals a tab's panel. Called by `on:click="/select/<index>"`, and by
   * theme-editor.js when the merchant selects a tab block in the sidebar.
   *
   * @param {number} index - The index of the tab to reveal.
   */
  select(index) {
    const { tabs } = this.refs;
    if (!tabs?.length) return;
    if (typeof index !== 'number' || index < 0 || index >= tabs.length) return;
    if (index === this.#active) return;

    this.#active = index;

    this.#render();
  }

  /**
   * Roving tabindex with activation on focus (WAI-ARIA "tabs with automatic
   * activation"). ArrowLeft/ArrowRight are physical and this storefront is
   * bilingual, so the step is resolved from the computed writing direction
   * rather than hard-coded.
   *
   * @param {KeyboardEvent} event - The keydown event.
   */
  handleKeydown(event) {
    const { tabs } = this.refs;
    const target = event.target;

    if (!tabs?.length || !(target instanceof HTMLElement)) return;
    if (target.getAttribute('role') !== 'tab') return;

    const index = tabs.indexOf(target);
    if (index === -1) return;

    const forward = getComputedStyle(this).direction === 'rtl' ? -1 : 1;

    /** @type {Record<string, number>} */
    const moves = {
      ArrowRight: forward,
      ArrowLeft: -forward,
      Home: -index,
      End: tabs.length - 1 - index,
    };

    const offset = moves[event.key];
    if (offset === undefined) return;

    event.preventDefault();

    const next = (index + offset + tabs.length) % tabs.length;

    this.select(next);
    tabs[next]?.focus();
  }

  #render() {
    const { tabs, panels } = this.refs;

    for (const [index, tab] of tabs?.entries() ?? []) {
      const isActive = index === this.#active;

      tab.setAttribute('aria-selected', String(isActive));
      tab.setAttribute('tabindex', isActive ? '0' : '-1');
    }

    for (const [index, panel] of panels?.entries() ?? []) {
      panel.toggleAttribute('hidden', index !== this.#active);
    }
  }
}

if (!customElements.get('product-tabs-component')) {
  customElements.define('product-tabs-component', ProductTabsComponent);
}
